import {
  Button,
  makeStaticStyles,
  makeStyles,
  Portal,
  shorthands,
  tokens,
  useModalAttributes,
} from "@fluentui/react-components";
import { CursorClick20Regular, Dismiss16Regular, Map20Regular } from "@fluentui/react-icons";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  AnchoredStep,
  anchorIndex,
  findStepElement,
  HELP_PANEL_ATTR,
  HELP_Z,
  stepAtElement,
} from "./dom";
import { cardPosition, Rect, rectOf } from "./position";
import type { HelpLanguage, HelpStep, PageHelp } from "./types";
import { localizedSection, localizedStep } from "./types";

/**
 * "Point at a field" — "what is THIS control?", answered by pointing at it. A user looking at a
 * field they do not understand already knows where it is; asking them to name it or count to it
 * in a list is asking the wrong question. Here the screen itself is the index.
 *
 * Three things make it safe to run over a half-filled form:
 *  - **Every page interaction is swallowed** in the CAPTURE phase at the window — the whole
 *    press sequence, not just `click` (a Fluent Dropdown / react-select opens on `mousedown`),
 *    so asking what a control does can never press it.
 *  - **Nothing is dimmed** — a scrim would hide the form being read.
 *  - **The guide's own surfaces are exempt** (`data-help-panel`), so its buttons keep working.
 */

const CARD_WIDTH = 330;
const CARD_HEIGHT = 190;
/** How long "nothing documented here" stays up before it stops being an answer and starts nagging. */
const MISS_MS = 2200;

const HOVER_COLOR = "#f0a028";
const INSPECTING_CLASS = "hbh-help-inspecting";

/** The `help` cursor everywhere while the mode is on, put back on the guide's own surfaces. */
const useInspectCursor = makeStaticStyles({
  [`body.${INSPECTING_CLASS} *`]: { cursor: "help !important" },
  [`body.${INSPECTING_CLASS} [data-help-panel] *`]: { cursor: "auto !important" },
  [`body.${INSPECTING_CLASS} [data-help-panel] button, body.${INSPECTING_CLASS} [data-help-panel] button *`]:
    { cursor: "pointer !important" },
});

const useStyles = makeStyles({
  highlight: {
    position: "fixed",
    zIndex: HELP_Z.overlay,
    ...shorthands.borderRadius("4px"),
    pointerEvents: "none",
    transitionProperty: "top, left, width, height",
    transitionDuration: "80ms",
    transitionTimingFunction: "ease",
  },
  hover: {
    ...shorthands.border("2px", "dashed", HOVER_COLOR),
  },
  pinned: {
    ...shorthands.border("2px", "solid", tokens.colorBrandStroke1),
    backgroundColor: "rgba(90, 45, 130, 0.06)",
  },
  banner: {
    position: "fixed",
    top: "14px",
    left: "50%",
    transform: "translateX(-50%)",
    zIndex: HELP_Z.banner,
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    maxWidth: "calc(100vw - 32px)",
    boxSizing: "border-box",
    backgroundColor: "#0f172a",
    color: "#ffffff",
    ...shorthands.borderRadius(tokens.borderRadiusCircular),
    paddingLeft: "16px",
    paddingRight: "6px",
    paddingTop: "4px",
    paddingBottom: "4px",
    boxShadow: "0 8px 24px rgba(15, 23, 42, 0.35)",
    lineHeight: "normal",
  },
  bannerLabel: {
    fontSize: "12.5px",
    fontWeight: tokens.fontWeightSemibold,
    whiteSpace: "nowrap",
    overflowX: "hidden",
    overflowY: "hidden",
    textOverflow: "ellipsis",
  },
  bannerKey: {
    fontSize: "11px",
    opacity: 0.65,
    flexShrink: 0,
    "@media (max-width: 640px)": {
      display: "none",
    },
  },
  bannerClose: {
    color: "#ffffff",
    flexShrink: 0,
    ":hover": {
      color: "#ffffff",
    },
  },
  miss: {
    position: "fixed",
    width: "240px",
    zIndex: HELP_Z.banner,
    boxSizing: "border-box",
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground2,
    ...shorthands.border("1px", "solid", tokens.colorNeutralStroke2),
    ...shorthands.borderRadius("12px"),
    ...shorthands.padding("8px", "12px"),
    boxShadow: "0 6px 18px rgba(15, 23, 42, 0.22)",
    pointerEvents: "none",
    fontSize: "12px",
    lineHeight: 1.5,
    textAlign: "left",
  },
  card: {
    position: "fixed",
    width: `${CARD_WIDTH}px`,
    zIndex: HELP_Z.card,
    boxSizing: "border-box",
    backgroundColor: tokens.colorNeutralBackground1,
    color: tokens.colorNeutralForeground1,
    ...shorthands.borderRadius("16px"),
    ...shorthands.padding("16px"),
    boxShadow: "0 12px 32px rgba(15, 23, 42, 0.28)",
    textAlign: "left",
    lineHeight: "normal",
  },
  section: {
    fontSize: "10px",
    fontWeight: tokens.fontWeightBold,
    letterSpacing: "0.8px",
    textTransform: "uppercase",
    color: tokens.colorNeutralForeground3,
    whiteSpace: "nowrap",
    overflowX: "hidden",
    overflowY: "hidden",
    textOverflow: "ellipsis",
  },
  title: {
    fontSize: "14.5px",
    fontWeight: tokens.fontWeightSemibold,
    marginTop: "2px",
  },
  body: {
    fontSize: "13px",
    lineHeight: 1.55,
    color: tokens.colorNeutralForeground2,
    marginTop: "6px",
  },
  hint: {
    fontSize: "11.5px",
    lineHeight: 1.5,
    fontStyle: "italic",
    color: tokens.colorNeutralForeground3,
    marginTop: "6px",
  },
  actions: {
    display: "flex",
    alignItems: "center",
    columnGap: "8px",
    marginTop: "14px",
  },
  continue: {
    fontSize: "11.5px",
    marginLeft: "-6px",
    color: tokens.colorNeutralForeground2,
  },
  spacer: {
    flexGrow: 1,
  },
});

/** Outline drawn over a control. Never interactive — the real element has to stay hit-testable. */
const Highlight = ({ rect, className }: { rect: Rect; className: string }) => (
  <div
    aria-hidden
    className={className}
    style={{
      top: rect.top - 3,
      left: rect.left - 3,
      width: rect.width + 6,
      height: rect.height + 6,
    }}
  />
);

interface HelpInspectProps {
  /** Role-filtered steps for the current page — the same list the tour and drawer are given. */
  steps: HelpStep[];
  help: PageHelp;
  /** `PageHelp.contexts` in the reader's language, so a pinned step can say how it is reached. */
  contexts?: Record<string, string>;
  language: HelpLanguage;
  /** Continue as a normal walk from the field just explained. */
  onTourFrom: (step: HelpStep) => void;
  onExit: () => void;
}

export const HelpInspect = ({
  steps,
  help,
  contexts,
  language,
  onTourFrom,
  onExit,
}: HelpInspectProps) => {
  useInspectCursor();
  const styles = useStyles();
  const { modalAttributes } = useModalAttributes({ trapFocus: true, alwaysFocusable: true });
  const index = useMemo(() => anchorIndex(steps), [steps]);

  const [hover, setHover] = useState<AnchoredStep | null>(null);
  const [hoverRect, setHoverRect] = useState<Rect | null>(null);
  const [pinned, setPinned] = useState<AnchoredStep | null>(null);
  const [pinnedRect, setPinnedRect] = useState<Rect | null>(null);
  const [miss, setMiss] = useState<{ top: number; left: number } | null>(null);

  /** The element the pointer last resolved to, so a mouse move over the SAME control is free. */
  const hoveredEl = useRef<HTMLElement | null>(null);
  const missTimer = useRef(0);
  /** Read through a ref so the pointer listeners are not re-attached on every parent render. */
  const exitRef = useRef(onExit);
  exitRef.current = onExit;

  useEffect(() => {
    document.body.classList.add(INSPECTING_CLASS);
    return () => document.body.classList.remove(INSPECTING_CLASS);
  }, []);

  const remeasure = useCallback((): void => {
    // A table refetch re-renders rows: a detached node measures as 0×0 at the top-left corner,
    // so forget it rather than outlining nothing.
    if (hoveredEl.current && !hoveredEl.current.isConnected) {
      hoveredEl.current = null;
      setHover(null);
      setHoverRect(null);
    }
    if (hoveredEl.current) setHoverRect(rectOf(hoveredEl.current));
    if (pinned && !pinned.element.isConnected) {
      const again = findStepElement(pinned.step.selector);
      if (again) {
        setPinned({ step: pinned.step, element: again });
        setPinnedRect(rectOf(again));
      } else {
        setPinned(null);
        setPinnedRect(null);
      }
      return;
    }
    setPinnedRect((current) => (pinned && current ? rectOf(pinned.element) : current));
  }, [pinned]);

  useEffect(() => {
    window.addEventListener("resize", remeasure);
    window.addEventListener("scroll", remeasure, true);
    return () => {
      window.removeEventListener("resize", remeasure);
      window.removeEventListener("scroll", remeasure, true);
    };
  }, [remeasure]);

  useEffect(() => {
    const own = (target: EventTarget | null): boolean =>
      target instanceof Element && target.closest(`[${HELP_PANEL_ATTR}]`) !== null;

    const onMove = (event: PointerEvent): void => {
      const found = stepAtElement(event.target, index);
      if (found?.element === hoveredEl.current) return;
      hoveredEl.current = found?.element ?? null;
      setHover(found);
      setHoverRect(found ? rectOf(found.element) : null);
    };

    /**
     * The page is inert while the mode is on — the whole press sequence, because a dropdown
     * opens on `mousedown` and a menu item commits on `mouseup`.
     */
    const swallow = (event: Event): void => {
      if (own(event.target)) return;
      event.preventDefault();
      event.stopPropagation();
    };

    const onClick = (event: MouseEvent): void => {
      if (own(event.target)) return;
      event.preventDefault();
      event.stopPropagation();
      const found = stepAtElement(event.target, index);
      if (!found) {
        // Said out loud rather than ignored: a click that does nothing reads as the mode being
        // broken, when the truth is that this control is not documented yet.
        window.clearTimeout(missTimer.current);
        setMiss({ top: event.clientY, left: event.clientX });
        missTimer.current = window.setTimeout(() => setMiss(null), MISS_MS);
        return;
      }
      setMiss(null);
      setPinned(found);
      setPinnedRect(rectOf(found.element));
    };

    const onKey = (event: KeyboardEvent): void => {
      if (event.key !== "Escape") return;
      // Capture + stop, like the tour: leaving must not also dismiss the dialog underneath.
      event.stopPropagation();
      event.preventDefault();
      exitRef.current();
    };

    const PRESS_EVENTS = [
      "pointerdown",
      "pointerup",
      "mousedown",
      "mouseup",
      "dblclick",
      "contextmenu",
      // A middle click opens a link in a new tab on `auxclick`, not on `click`.
      "auxclick",
    ];
    window.addEventListener("pointermove", onMove, true);
    window.addEventListener("click", onClick, true);
    window.addEventListener("keydown", onKey, true);
    for (const name of PRESS_EVENTS) window.addEventListener(name, swallow, true);

    const timer = missTimer;
    return () => {
      window.clearTimeout(timer.current);
      window.removeEventListener("pointermove", onMove, true);
      window.removeEventListener("click", onClick, true);
      window.removeEventListener("keydown", onKey, true);
      for (const name of PRESS_EVENTS) window.removeEventListener(name, swallow, true);
    };
  }, [index]);

  const card = pinnedRect ? cardPosition(pinnedRect, { width: CARD_WIDTH, height: CARD_HEIGHT }) : null;
  const contextHint = pinned?.step.context ? contexts?.[pinned.step.context] : undefined;
  const showHover = hoverRect !== null && hover?.element !== pinned?.element;
  const pinnedText = pinned ? localizedStep(pinned.step, language) : null;

  return (
    <Portal>
      {showHover && hoverRect && (
        <Highlight rect={hoverRect} className={`${styles.highlight} ${styles.hover}`} />
      )}
      {pinnedRect && (
        <Highlight rect={pinnedRect} className={`${styles.highlight} ${styles.pinned}`} />
      )}

      <div data-help-panel="inspect-banner" className={styles.banner}>
        <CursorClick20Regular style={{ flexShrink: 0 }} />
        <span className={styles.bannerLabel}>
          {hover ? localizedStep(hover.step, language).title : "Click any field to read what it does"}
        </span>
        <span className={styles.bannerKey}>Esc to leave</span>
        <Button
          appearance="transparent"
          size="small"
          icon={<Dismiss16Regular />}
          onClick={onExit}
          aria-label="Leave point-at-a-field mode"
          className={styles.bannerClose}
        />
      </div>

      {miss && (
        <div
          aria-live="polite"
          data-help-panel="inspect-miss"
          className={styles.miss}
          style={{
            top: Math.min(miss.top + 14, window.innerHeight - 60),
            left: Math.min(miss.left + 14, window.innerWidth - 260),
          }}
        >
          No guide entry for this control yet — try the field itself, or the panel’s page guide.
        </div>
      )}

      {pinned && pinnedText && card && (
        <div
          role="dialog"
          data-help-panel="inspect"
          aria-label={`What this does: ${pinnedText.title}`}
          className={styles.card}
          style={{ top: card.top, left: card.left }}
          {...modalAttributes}
        >
          {pinned.step.section && (
            <div className={styles.section}>
              {localizedSection(help, pinned.step.section, language)}
            </div>
          )}
          <div className={styles.title}>{pinnedText.title}</div>
          <div className={styles.body}>{pinnedText.body}</div>
          {contextHint && <div className={styles.hint}>{contextHint}</div>}

          <div className={styles.actions}>
            <Button
              appearance="transparent"
              size="small"
              icon={<Map20Regular />}
              onClick={() => onTourFrom(pinned.step)}
              data-help="help-inspect-tour"
              className={styles.continue}
            >
              Continue from here
            </Button>
            <div className={styles.spacer} />
            <Button appearance="primary" size="small" onClick={onExit}>
              Done
            </Button>
          </div>
        </div>
      )}
    </Portal>
  );
};
